import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { FloatingWhatsApp } from "../components/site/FloatingWhatsApp";
import { GoldDivider } from "../components/site/Ornament";
import { useLang } from "../i18n/LanguageContext";
import { S } from "../data/strings";

const POLICIES = {
  privacy: {
    title: { en: "Privacy Policy", hi: "गोपनीयता नीति" },
    body: {
      en: [
        "At Nikunj Ittar Wale, we deeply respect your privacy. This website does not host any contact or enquiry forms and does not collect or store your data on any server.",
        "All enquiries are made directly through WhatsApp, phone or email. Any details you choose to share with us on those channels are used solely to respond to your enquiry and fulfil your request. We do not sell, rent or share your information with third parties.",
        "By contacting us via WhatsApp, phone or email, you consent to us responding to you regarding your enquiry.",
      ],
      hi: [
        "निकुंज इत्र वाले में हम आपकी गोपनीयता का गहरा सम्मान करते हैं। यह वेबसाइट किसी भी संपर्क या पूछताछ फ़ॉर्म का उपयोग नहीं करती और आपका कोई डेटा किसी सर्वर पर संग्रहीत नहीं करती।",
        "सभी पूछताछ सीधे व्हाट्सएप, फ़ोन या ईमेल के माध्यम से होती हैं। इन माध्यमों पर आपके द्वारा साझा किया गया विवरण केवल आपकी पूछताछ का उत्तर देने हेतु उपयोग होता है। हम आपकी जानकारी किसी तीसरे पक्ष को नहीं बेचते या साझा करते।",
        "व्हाट्सएप, फ़ोन या ईमेल द्वारा संपर्क करने पर, आप हमें आपकी पूछताछ हेतु उत्तर देने की सहमति देते हैं।",
      ],
    },
  },
  terms: {
    title: { en: "Terms & Conditions", hi: "नियम व शर्तें" },
    body: {
      en: [
        "This website showcases the products and craftsmanship of Nikunj Ittar Wale. All prices shown are indicative and may vary based on customisation, fabric and availability.",
        "Orders and enquiries are confirmed directly via WhatsApp or phone. Custom poshaks are handcrafted to order and require confirmation of measurements before production begins.",
        "All designs, images and content on this website are the property of Nikunj Ittar Wale and may not be reproduced without permission.",
      ],
      hi: [
        "यह वेबसाइट निकुंज इत्र वाले के उत्पादों व शिल्पकला को प्रदर्शित करती है। दर्शाए गए सभी मूल्य सांकेतिक हैं व कस्टमाइज़ेशन, कपड़े व उपलब्धता के अनुसार बदल सकते हैं।",
        "ऑर्डर व पूछताछ सीधे व्हाट्सएप या फ़ोन द्वारा पुष्टि की जाती हैं। कस्टम पोशाक ऑर्डर पर हस्तनिर्मित होती हैं।",
        "इस वेबसाइट की सभी डिज़ाइन, छवियाँ व सामग्री निकुंज इत्र वाले की संपत्ति हैं।",
      ],
    },
  },
  shipping: {
    title: { en: "Shipping Policy", hi: "शिपिंग नीति" },
    body: {
      en: [
        "We ship across India and to select international destinations. Ready-made items are usually dispatched within 2–4 working days of order confirmation.",
        "Custom poshaks require additional crafting time, typically 5–10 working days depending on the design and detailing.",
        "Shipping charges and delivery timelines are confirmed at the time of order via WhatsApp.",
      ],
      hi: [
        "हम पूरे भारत में व चुनिंदा अंतरराष्ट्रीय गंतव्यों पर शिपिंग करते हैं। रेडीमेड वस्तुएँ आमतौर पर ऑर्डर पुष्टि के 2–4 कार्यदिवसों में भेजी जाती हैं।",
        "कस्टम पोशाक हेतु अतिरिक्त समय लगता है, आमतौर पर 5–10 कार्यदिवस।",
        "शिपिंग शुल्क व डिलीवरी समय ऑर्डर के समय व्हाट्सएप द्वारा पुष्ट किए जाते हैं।",
      ],
    },
  },
  returns: {
    title: { en: "Return Policy", hi: "वापसी नीति" },
    body: {
      en: [
        "As our poshaks and ittars are delicate, devotional and often customised, returns are accepted only in the case of a manufacturing defect or damage during transit.",
        "Please report any issue within 48 hours of delivery with clear photographs via WhatsApp.",
        "Customised products are non-returnable unless they arrive damaged or significantly different from what was confirmed.",
      ],
      hi: [
        "चूँकि हमारी पोशाक व इत्र नाज़ुक, भक्तिपूर्ण व अक्सर कस्टमाइज़्ड होते हैं, वापसी केवल निर्माण दोष या परिवहन क्षति की स्थिति में स्वीकार की जाती है।",
        "कृपया डिलीवरी के 48 घंटे के भीतर स्पष्ट तस्वीरों के साथ व्हाट्सएप पर किसी भी समस्या की सूचना दें।",
        "कस्टमाइज़्ड उत्पाद वापस नहीं किए जा सकते, जब तक कि वे क्षतिग्रस्त न आएं।",
      ],
    },
  },
};

export default function Policy() {
  const { slug } = useParams();
  const { t } = useLang();
  const policy = POLICIES[slug] || POLICIES.privacy;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${policy.title.en} | Nikunj Ittar Wale`;
  }, [slug, policy.title.en]);

  return (
    <>
      <Header />
      <main className="bg-ivory grain relative pt-32 pb-24 min-h-screen">
        <div className="mx-auto max-w-3xl px-6 sm:px-10">
          <Link to="/" data-testid="policy-back" className="inline-flex items-center gap-2 text-sm text-maroon/70 hover:text-maroon transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t(S.footer.home)}
          </Link>
          <h1 className="mt-8 font-serif font-light text-maroon text-4xl sm:text-5xl tracking-tight">{t(policy.title)}</h1>
          <GoldDivider className="mt-6 !justify-start" />
          <div className="mt-8 space-y-5">
            {t(policy.body).map((p, i) => (
              <p key={i} className="text-maroon/75 leading-relaxed text-sm sm:text-base">{p}</p>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}
